import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localeMk from '@angular/common/locales/mk';
import { APP_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { UrlSerializer } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CdcJsService } from '@spartacus/cdc/root';
import {
  CategoryPageMetaResolver,
  CMS_PAGE_NORMALIZER,
  CmsPageConnector,
  ComponentDecorator,
  ConfigModule,
  DeferLoadingStrategy,
  defaultPageMetaConfig,
  I18nextHttpBackendInitializer,
  I18nModule,
  LanguageInitializer,
  LanguageService,
  PageMetaConfig,
  PRODUCT_NORMALIZER,
  ProductLoadingService,
  ProductPageMetaResolver,
  provideConfig,
  RoutingService,
  StateTransferType,
  TokenRevocationInterceptor,
} from '@spartacus/core';
import { StoreFinderConfig } from '@spartacus/storefinder/core';
import {
  AppRoutingModule,
  BreadcrumbModule,
  DIALOG_TYPE,
  GenericLinkModule,
  JsonLdBuilderModule,
  LayoutConfig,
  OnNavigateConfig,
  OutletModule,
  PageMetaLinkService,
  PaginationConfig,
  ProductListComponentService,
  ProductSchemaBuilder,
  SCHEMA_BUILDER,
  StructuredDataModule,
} from '@spartacus/storefront';
import { TmsConfig } from '@spartacus/tracking/tms/core';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { MarkdownModule } from 'ngx-markdown';
import { AppComponent } from './app.component';
import { CustomCmsPageConnector } from './cmd/connectors/page/custom-cms-page.connector';
import { Constant } from './constant/constant';
import { FbqGgCartAddEntrySuccessEvent } from './events/analytics/fbq/cart/add/fbq-gg-cart-add-entry-success-event';
import { FbqGgCheckoutInitiateEvent } from './events/analytics/fbq/checkout/initiate/fbq-gg-checkout-initiate-event';
import { FbqGgCheckoutShippingAddressSelectedEvent } from './events/analytics/fbq/checkout/shipping-address/fbq-gg-checkout-shipping-address-selected-event';
import { FbqGgPostFormSuccessEvent } from './events/analytics/fbq/form/fbq-gg-post-form-success-event';
import { FbqGgOrderPlacedEvent } from './events/analytics/fbq/order/fbq-gg-order-placed-event';
import { FbqGgCartPageEvent } from './events/analytics/fbq/page/cart/fbq-gg-cart-page-event';
import { FbqGgCategoryPageEvent } from './events/analytics/fbq/page/category/fbq-gg-category-page-event';
import { FbqGgProductPageEvent } from './events/analytics/fbq/page/product/fbq-gg-product-page-event';
import { FbqGgSearchPageEvent } from './events/analytics/fbq/page/search/fbq-gg-search-page-event';
import { FbqGgUserRegistrationSuccessEvent } from './events/analytics/fbq/user/registration/fbq-gg-user-registration-success-event';
import { Ga4GgCartAddEntrySuccessEvent } from './events/analytics/ga4/cart/add/ga4-gg-cart-add-entry-success-event';
import { Ga4GgCartDiscountEvent } from './events/analytics/ga4/cart/ga4-gg-cart-discount.event';
import { Ga4GgCartRemoveEntrySuccessEvent } from './events/analytics/ga4/cart/remove/ga4-gg-cart-remove-entry-success-event';
import { Ga4GgCartUpdateEntrySuccessEvent } from './events/analytics/ga4/cart/update/ga4-gg-cart-update-entry-success-event';
import { Ga4GgCheckoutInitiateEvent } from './events/analytics/ga4/checkout/initiate/ga4-gg-checkout-initiate-event';
import { Ga4GgCheckoutPaymentMethodSelectedEvent } from './events/analytics/ga4/checkout/payment-method/ga4-gg-checkout-payment-method-selected-event';
import { Ga4GgCheckoutShippingAddressSelectedEvent } from './events/analytics/ga4/checkout/shipping-address/ga4-gg-checkout-shipping-address-selected-event';
import { Ga4GgFindADealerClickEvent } from './events/analytics/ga4/find-a-dealer/ga4-gg-find-a-dealer-click.event';
import { Ga4GgFindADealerSearchEvent } from './events/analytics/ga4/find-a-dealer/ga4-gg-find-a-dealer-search.event';
import { Ga4GgAnchorClickEvent } from './events/analytics/ga4/general/ga4-gg-anchor-click.event';
import { Ga4GgButtonClickEvent } from './events/analytics/ga4/general/ga4-gg-button-click.event';
import { Ga4GgDownloadEvent } from './events/analytics/ga4/general/ga4-gg-download.event';
import { Ga4GgReaderTypeEvent } from './events/analytics/ga4/general/ga4-gg-reader-type.event';
import { Ga4GgScrollEvent } from './events/analytics/ga4/general/ga4-gg-scroll.event';
import { Ga4GgSliderViewsEvent } from './events/analytics/ga4/general/ga4-gg-slider-views.event';
import { Ga4GgCategoryListingFacetSelectEvent } from './events/analytics/ga4/listing/ga4-gg-category-listing-facet-select.event';
import { Ga4GgListingProductSelectEvent } from './events/analytics/ga4/listing/ga4-gg-listing-product-select.event';
import { Ga4GgListingProductsViewEvent } from './events/analytics/ga4/listing/ga4-gg-listing-products-view.event';
import { Ga4GgNavigationSelectEvent } from './events/analytics/ga4/navigation/ga4-gg-navigation-select.event';
import { Ga4GgNewsletterSubscriptionSuccessEvent } from './events/analytics/ga4/newsletter/ga4-gg-newsletter-subscription-success.event';
import { Ga4GgPurchaseEvent } from './events/analytics/ga4/order/ga4-gg-purchase-event';
import { Ga4GgCartPageEvent } from './events/analytics/ga4/page/cart/ga4-gg-cart-page-event';
import { Ga4GgCheckoutReviewPageEvent } from './events/analytics/ga4/page/checkout/ga4-gg-checkout-review-page-event';
import { Ga4GgListingPageEvent } from './events/analytics/ga4/page/listing/ga4-gg-listing-page-event';
import { Ga4GgProductPageEvent } from './events/analytics/ga4/page/product/ga4-gg-product-page-event';
import { Ga4GgProductEnlargeImageEvent } from './events/analytics/ga4/product/ga4-gg-product-enlarge-image.event';
import { Ga4GgProductFindADealerEvent } from './events/analytics/ga4/product/ga4-gg-product-find-a-dealer.event';
import { Ga4GgProductManualDownloadEvent } from './events/analytics/ga4/product/ga4-gg-product-manual-download.event';
import { Ga4GgProductSliderImageEvent } from './events/analytics/ga4/product/ga4-gg-product-slider-image.event';
import { Ga4GgProductSpecificationClickEvent } from './events/analytics/ga4/product/ga4-gg-product-specification-click.event';
import { Ga4GgProductWhereToBuyPartnerEvent } from './events/analytics/ga4/product/ga4-gg-product-where-to-buy-partner.event';
import { Ga4GgProductWhereToBuyEvent } from './events/analytics/ga4/product/ga4-gg-product-where-to-buy.event';
import { Ga4GgSearchViewAutoCompleteResultsEvent } from './events/analytics/ga4/search/ga4-gg-search-view-auto-complete-results.event';
import { Ga4GgUserLoginEvent } from './events/analytics/ga4/user/login/ga4-gg-user-login.event';
import { Ga4GgUserRegistrationSuccessEvent } from './events/analytics/ga4/user/registration/ga4-gg-user-registration-success-event';
import { GtmGgCartAddEntrySuccessEvent } from './events/analytics/gtm/cart/add/gtm-gg-cart-add-entry-success-event';
import { GtmGgCartRemoveEntrySuccessEvent } from './events/analytics/gtm/cart/remove/gtm-gg-cart-remove-entry-success-event';
import { GtmGgCartUpdateEntrySuccessEvent } from './events/analytics/gtm/cart/update/gtm-gg-cart-update-entry-success-event';
import { GtmGgListingProductSelectEvent } from './events/analytics/gtm/listing/gtm-gg-listing-product-select-event';
import { GtmGgOrderPlacedEvent } from './events/analytics/gtm/order/gtm-gg-order-placed-event';
import { GtmGgCartPageEvent } from './events/analytics/gtm/page/cart/gtm-gg-cart-page-event';
import { GtmGgCategoryPageEvent } from './events/analytics/gtm/page/category/gtm-gg-category-page-event';
import { GtmGgCheckoutPageEvent } from './events/analytics/gtm/page/checkout/gtm-gg-checkout-page-event';
import { GtmGgProductPageEvent } from './events/analytics/gtm/page/product/gtm-gg-product-page-event';
import { GtmGgSearchPageEvent } from './events/analytics/gtm/page/search/gtm-gg-search-page-event';
import { EventsModule } from './events/events.module';
import { CmsStaticModule } from './features/cms/static/cms-static.module';
import { ComponentsModule } from './features/components/components.module';
import { GgCookieConsentComponent } from './features/components/gg-cookie-consent/gg-cookie-consent.component';
import { ProductSchemaComponent } from './features/components/product/product-schema/product-schema.component';
import { DataBindingsModule } from './features/data-bindings/data-bindings.module';
import { CustomCategoryPageMetaResolver } from './features/resolver/custom-category-page-meta.resolver';
import { CustomProductPageMetaResolver } from './features/resolver/custom-product-page-meta.resolver';
import { CustomRoutingService } from './features/resolver/custom-routing-service';
import { CustomPageNormalizer } from './features/routing/custom-page.normalizer';
import { CustomProductNormalizer } from './features/routing/custom-product.normalizer';
import { CustomUrlSerializer } from './features/routing/custom-url.serializer';
import { customProductUrlMatcher } from './features/routing/product-url.matcher';
import { ConfigService } from './features/services/config.service';
import { FbqCollectorService } from './features/services/fbq-collector.service';
import { Ga4CollectorService } from './features/services/ga4-collector.service';
import { GgProductListComponentService } from './features/services/gg-product-list-component.service';
import { GgProductLoadingService } from './features/services/gg-product-loading.service';
import { GtmCollectorService } from './features/services/gtm-collector.service';
import { HjCollectorService } from './features/services/hj-collector.service';
import { CustomCdcJsService } from './features/services/overrides/custom-cdc-js.service';
import { HisenseI18nextHttpBackendInitializer } from './features/services/spartacus/i18next-http-backend-initializer';
import { TwqCollectorService } from './features/services/twq-collector.service';
import { HttpRemoveForcedParametersInterceptor } from './interceptors/http-remove-forced-parameters.interceptor';
import { CustomTokenRevocationInterceptor } from './interceptors/tokenRevocation.interceptor';
import { ItemListSchemaBuilder } from './seo/builders/item-list-schema.builder';
import { HisenseProductSchemaBuilder } from './seo/builders/product-schema-builder';
import { WebSiteSchemaBuilder } from './seo/builders/web-site-schema.builder';
import { CustomPageMetaLinkService } from './seo/custom-page-meta-link.service';
import { CustomLanguageInitializer } from './site-context/language/custom-language-initializer';
import { CustomLanguageService } from './site-context/language/custom-language.service';
import { SpartacusModule } from './spartacus/spartacus.module';
import { GgChatWidgetComponent } from './features/components/chat/gg-chat-widget.component';
import { GGComponentDecorator } from './features/services/gg-data-component-decorator';
import { IAdvizeDataModule } from './features/integration/iAdvize/i-advize-data.module';

registerLocaleData(localeMk);

function appLoadFactory(config: ConfigService) {
  return () => config.loadConfig();
}

@NgModule({
  declarations: [AppComponent, ProductSchemaComponent],
  imports: [
    BrowserModule,
    ConfigModule.withConfig({
      launch: {
        ANONYMOUS_CONSENT: {
          inlineRoot: true,
          component: GgCookieConsentComponent,
          dialogType: DIALOG_TYPE.DIALOG,
        },
      },
    }),
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    AppRoutingModule,
    SpartacusModule,
    GgChatWidgetComponent,
    I18nModule,
    CmsStaticModule,
    BreadcrumbModule,
    JsonLdBuilderModule,
    RecaptchaV3Module,
    MarkdownModule.forRoot(),

    // Enable Spartacus SSR Transfer State to avoid duplicate client refetches after hydration.
    ConfigModule.withConfig({
      state: {
        ssrTransfer: {
          keys: {
            products: StateTransferType.TRANSFER_STATE,
            cms: StateTransferType.TRANSFER_STATE,
            user: StateTransferType.TRANSFER_STATE,
            cart: StateTransferType.TRANSFER_STATE,
            facets: StateTransferType.TRANSFER_STATE,
            baseSite: StateTransferType.TRANSFER_STATE,
            language: StateTransferType.TRANSFER_STATE,
            currency: StateTransferType.TRANSFER_STATE,
          },
        },
      },
    }),

    ConfigModule.withConfig({
      breakpoints: {
        xs: 576,
        sm: 768,
        md: 1024,
        lg: 1200,
        xl: 1440,
        xxl: 1600,
      },
      deferredLoading: {
        strategy: DeferLoadingStrategy.DEFER,
        intersectionMargin: '200px',
      },
      routing: {
        routes: {
          product: {
            matchers: [customProductUrlMatcher],
            paths: ['/:productUrl'],
          },
        },
      },
      // cmsStructure: {
      // componentId: 'HamburgerMenuComponent',
      // pageSlotPosition: 'HeaderLinks'
      // }
    } as LayoutConfig),

    ConfigModule.withConfig({
      googleMaps: {
        apiKey: 'AIzaSyDaAuZGR8d9SMb_irRn-sIEHsSepjUmDVQ',
        apiUrl: 'https://maps.googleapis.com/maps/api/js',
        scale: 7,
        selectedMarkerScale: 17,
        radius: 50000,
      },
    } as StoreFinderConfig),

    ConfigModule.withConfig({
      pagination: {
        addFirst: true,
        addLast: true,
        addDots: true,
        addNext: true,
        addPrevious: true,
      },
    } as PaginationConfig),
    ComponentsModule,
    DataBindingsModule,
    ConfigModule.withConfig({
      ...defaultPageMetaConfig,
      pageMeta: {
        resolvers: [
          {
            property: 'title',
            method: 'resolveTitle',
          },
          {
            property: 'heading',
            method: 'resolveHeading',
          },
          {
            property: 'breadcrumbs',
            method: 'resolveBreadcrumbs',
          },
          {
            property: 'description',
            method: 'resolveDescription',
          },
          {
            property: 'image',
            method: 'resolveImage',
            disabledInCsr: false,
          },
          {
            property: 'robots',
            method: 'resolveRobots',
            disabledInCsr: false,
          },
          {
            property: 'canonicalUrl',
            method: 'resolveCanonicalUrl',
            disabledInCsr: false,
          },
        ],
        canonicalUrl: {
          forceHttps: true,
          forceWww: false,
          removeQueryParams: true,
          forceTrailingSlash: false,
        },
      },
    } as PageMetaConfig),
    ConfigModule.withConfig({
      tagManager: {
        gtm: {
          collector: GtmCollectorService,
          events: [
            GtmGgProductPageEvent,
            GtmGgCategoryPageEvent,
            GtmGgSearchPageEvent,
            GtmGgCartPageEvent,
            GtmGgOrderPlacedEvent,
            GtmGgCheckoutPageEvent,
            GtmGgCartAddEntrySuccessEvent,
            GtmGgCartRemoveEntrySuccessEvent,
            GtmGgCartUpdateEntrySuccessEvent,
            GtmGgListingProductSelectEvent,
          ],
        },
        fbq: {
          collector: FbqCollectorService,
          events: [
            FbqGgProductPageEvent,
            FbqGgCategoryPageEvent,
            FbqGgSearchPageEvent,
            FbqGgCartPageEvent,
            FbqGgOrderPlacedEvent,
            FbqGgCartAddEntrySuccessEvent,
            FbqGgCheckoutShippingAddressSelectedEvent,
            FbqGgUserRegistrationSuccessEvent,
            FbqGgPostFormSuccessEvent,
            FbqGgCheckoutInitiateEvent,
          ],
        },
        ga4: {
          collector: Ga4CollectorService,
          events: [
            Ga4GgPurchaseEvent,
            Ga4GgProductWhereToBuyEvent,
            Ga4GgProductWhereToBuyPartnerEvent,
            Ga4GgProductPageEvent,
            Ga4GgCartAddEntrySuccessEvent,
            Ga4GgCartUpdateEntrySuccessEvent,
            Ga4GgCartRemoveEntrySuccessEvent,
            Ga4GgCheckoutReviewPageEvent,
            Ga4GgCheckoutInitiateEvent,
            Ga4GgCheckoutPaymentMethodSelectedEvent,
            Ga4GgCheckoutShippingAddressSelectedEvent,
            Ga4GgUserLoginEvent,
            Ga4GgListingProductSelectEvent,
            Ga4GgUserRegistrationSuccessEvent,
            Ga4GgCartPageEvent,
            Ga4GgListingProductsViewEvent,
            Ga4GgFindADealerClickEvent,
            Ga4GgFindADealerSearchEvent,
            Ga4GgNavigationSelectEvent,
            Ga4GgNewsletterSubscriptionSuccessEvent,
            Ga4GgCategoryListingFacetSelectEvent,
            Ga4GgProductFindADealerEvent,
            Ga4GgSearchViewAutoCompleteResultsEvent,
            Ga4GgProductManualDownloadEvent,
            Ga4GgProductEnlargeImageEvent,
            Ga4GgProductSliderImageEvent,
            Ga4GgProductSpecificationClickEvent,
            Ga4GgSliderViewsEvent,
            Ga4GgCartDiscountEvent,
            Ga4GgScrollEvent,
            Ga4GgReaderTypeEvent,
            Ga4GgButtonClickEvent,
            Ga4GgAnchorClickEvent,
            Ga4GgDownloadEvent,
            Ga4GgListingPageEvent,
          ],
        },
        twq: {
          collector: TwqCollectorService,
        },
        hj: {
          collector: HjCollectorService,
        },
      },
    } as TmsConfig),
    EventsModule,
    GenericLinkModule,
    // for cxOutletPos directive
    OutletModule,
    // for cxJsonLd directive
    StructuredDataModule,
    IAdvizeDataModule,
  ],
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
    {
      provide: CdcJsService,
      useClass: CustomCdcJsService,
    },
    {
      provide: APP_ID,
      useValue: 'serverApp',
    },
    {
      provide: PRODUCT_NORMALIZER,
      useClass: CustomProductNormalizer,
      multi: true,
    },
    {
      provide: CMS_PAGE_NORMALIZER,
      useClass: CustomPageNormalizer,
      multi: true,
    },
    {
      provide: UrlSerializer,
      useClass: CustomUrlSerializer,
    },
    {
      provide: ProductPageMetaResolver,
      useClass: CustomProductPageMetaResolver,
    },
    {
      provide: CategoryPageMetaResolver,
      useClass: CustomCategoryPageMetaResolver,
    },
    {
      provide: LanguageService,
      useClass: CustomLanguageService,
    },
    {
      provide: LanguageInitializer,
      useClass: CustomLanguageInitializer,
    },
    {
      provide: I18nextHttpBackendInitializer,
      useClass: HisenseI18nextHttpBackendInitializer,
    },
    {
      provide: CmsPageConnector,
      useClass: CustomCmsPageConnector,
    },
    {
      provide: RoutingService,
      useClass: CustomRoutingService,
    },
    {
      provide: PageMetaLinkService,
      useClass: CustomPageMetaLinkService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRemoveForcedParametersInterceptor,
      multi: true,
    },
    {
      provide: TokenRevocationInterceptor,
      useClass: CustomTokenRevocationInterceptor,
    },
    {
      provide: ComponentDecorator,
      useExisting: GGComponentDecorator,
      multi: true,
    },
    [Constant],
    provideConfig(<OnNavigateConfig>{
      enableResetViewOnNavigate: {
        ignoreQueryString: true,
      },
    }),
    ProductSchemaComponent,
    provideConfig({
      seo: {
        structuredData: {
          disableInDevMode: false,
        },
      },
    }),
    {
      provide: ProductSchemaBuilder,
      useClass: HisenseProductSchemaBuilder,
    },
    {
      provide: SCHEMA_BUILDER,
      useExisting: WebSiteSchemaBuilder,
      multi: true,
    },
    {
      provide: SCHEMA_BUILDER,
      useExisting: ItemListSchemaBuilder,
      multi: true,
    },
    {
      provide: ProductLoadingService,
      useClass: GgProductLoadingService,
    },
    {
      provide: ProductListComponentService,
      useClass: GgProductListComponentService,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      deps: [ConfigService],
      useFactory: (config: ConfigService) => {
        return config.siteKey;
      },
    },
    provideAppInitializer(() => {
      const initializerFn = appLoadFactory(inject(ConfigService));
      return initializerFn();
    }),
    {
      provide: PageMetaLinkService,
      useClass: CustomPageMetaLinkService,
    },
    SsrCookieService,
    provideAnimationsAsync(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
